import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";

/**
 * FileInputLocalized — a wrapper around <input type="file"> that hides
 * the browser's native button (which is rendered in the OS locale, NOT
 * the app's i18n language) and shows a fully localized custom button
 * plus the selected filename or a translated "No file selected" text.
 *
 * Why we need this:
 *  The native file input picks up its labels ("Choose File" /
 *  "No file chosen") from the OS/browser locale. On a Romanian OS the
 *  user always sees "Alege fișierul / nu ai ales nici un fișier" even
 *  when the app UI is set to English, French, German, etc. — which
 *  breaks the i18n contract of the app. This component replaces the
 *  native chrome with an app-controlled label read from the current
 *  i18n locale ("chooseFile" + "noFileSelected", already present in
 *  all 7 locale JSON files).
 *
 * Props are intentionally compatible with the native <input type="file">
 * so callers can migrate with a one-line swap.
 */
export default function FileInputLocalized({
  accept,
  required = false,
  disabled = false,
  onChange,
  testId,
  className,
  label,
  buttonLabel,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");

  const handleChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFileName(f ? f.name : "");
    if (onChange) onChange(e);
  };

  return (
    <div className={className} data-testid={testId ? `${testId}-wrapper` : undefined}>
      {label ? <label className="label-lodge">{label}</label> : null}
      <div style={styles.row}>
        <button
          type="button"
          className="btn-outline-gold"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          data-testid={testId ? `${testId}-btn` : undefined}
          style={styles.btn}
        >
          <Upload size={13} />
          <span>{buttonLabel || t("documents.chooseFile", "Choose file")}</span>
        </button>
        <span style={styles.fileName} data-testid={testId ? `${testId}-name` : undefined}>
          {fileName || t("documents.noFileSelected", "No file selected")}
        </span>
      </div>
      {/* The native input is kept in the DOM (not `display:none` inside
          a hidden button) so the browser's `required` validation still
          picks it up when the parent form is submitted. */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        required={required}
        disabled={disabled}
        onChange={handleChange}
        data-testid={testId}
        style={styles.nativeInput}
        tabIndex={-1}
      />
    </div>
  );
}

const styles = {
  row: {
    display: "flex",
    alignItems: "center",
    gap: "0.7rem",
    minHeight: 38,
  },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    whiteSpace: "nowrap",
  },
  fileName: {
    fontSize: "0.78rem",
    color: "#7a7a78",
    fontStyle: "italic",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  nativeInput: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    whiteSpace: "nowrap",
    border: 0,
    opacity: 0,
    pointerEvents: "none",
  },
};
